import { SplashScreen } from "@capacitor/splash-screen";
import { type AnyRouter, useRouterState } from "@tanstack/react-router";
import { type JSX, type ReactNode, useEffect, useState } from "react";

import { Loading } from "@/components/ui/loading/loading";

/**
 * Se o boot nunca resolver (ex.: LaunchDarkly sem resposta), o splash nativo sai
 * mesmo assim e revela o `Loading` web, em vez de prender o app no splash.
 */
export const SPLASH_SCREEN_FALLBACK_MS = 10_000;

const hideSplashScreen = (): void => {
  void SplashScreen.hide();
};

const useInitialRouteReady = (router: AnyRouter): boolean => {
  const [isInitialRouteReady, setIsInitialRouteReady] = useState(false);
  // Quando o beforeLoad do root lança (ex.: "Usuário não autorizado") e uma rota
  // filha tem loader, o TanStack renderiza a página de erro mas nunca emite
  // "onResolved": a filha fica "pending" para sempre. Sem isto, o overlay cobriria
  // o erro e o app pareceria travado no loading.
  const hasRouteError = useRouterState({
    router,
    select: (state) => state.matches.some((match) => match.status === "error"),
  });

  useEffect(() => {
    if (isInitialRouteReady) {
      return undefined;
    }
    if (hasRouteError) {
      setIsInitialRouteReady(true);
      return undefined;
    }
    // O TanStack emite "onResolved" dentro de um useLayoutEffect interno, que
    // roda ANTES deste effect passivo. Se a rota inicial já resolveu, o
    // subscribe abaixo perderia o evento e o overlay ficaria preso — então
    // liberamos direto quando o router já está idle (resolvido).
    if (router.state.status === "idle" && router.state.resolvedLocation) {
      setIsInitialRouteReady(true);
      return undefined;
    }
    return router.subscribe("onResolved", () => {
      setIsInitialRouteReady(true);
    });
  }, [hasRouteError, isInitialRouteReady, router]);

  return isInitialRouteReady;
};

interface BootGateProps {
  router: AnyRouter;
  /** O app; `null` enquanto ainda não pode montar (ex.: LaunchDarkly carregando). */
  children: ReactNode;
}

/**
 * Cobre o boot até o router resolver a carga inicial (beforeLoad async do root +
 * primeira rota) ou até alguma rota falhar, para o erro ficar visível: mantém o
 * splash nativo e, embaixo dele, um único `Loading`.
 *
 * O `Loading` fica sempre na mesma posição da árvore, com ou sem `children`, para
 * não ser desmontado e remontado quando o app monta — senão aparecem duas telas de
 * loading seguidas, com a animação reiniciando. Cobre só o boot: depois do primeiro
 * onResolved nunca mais reaparece, então não afeta as navegações internas.
 */
export const BootGate = ({ router, children }: BootGateProps): JSX.Element => {
  const isInitialRouteReady = useInitialRouteReady(router);

  useEffect(() => {
    if (isInitialRouteReady) {
      hideSplashScreen();
      return undefined;
    }
    const fallback = setTimeout(hideSplashScreen, SPLASH_SCREEN_FALLBACK_MS);
    return () => clearTimeout(fallback);
  }, [isInitialRouteReady]);

  return (
    <>
      {children}
      {!isInitialRouteReady && <Loading />}
    </>
  );
};
